<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { resolveProductsOrdersFeatures } from 'dashboard/helper/productsOrdersFeatures';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const isSubmitting = ref(false);
const features = ref(resolveProductsOrdersFeatures());

const featureItems = computed(() => [
  {
    key: 'openConversationLink',
    title: t('GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.OPEN_CONVERSATION_LINK.TITLE'),
    description: t(
      'GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.OPEN_CONVERSATION_LINK.DESCRIPTION'
    ),
  },
  {
    key: 'timeline',
    title: t('GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.TIMELINE.TITLE'),
    description: t(
      'GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.TIMELINE.DESCRIPTION'
    ),
  },
  {
    key: 'advancedStatuses',
    title: t('GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.ADVANCED_STATUSES.TITLE'),
    description: t(
      'GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.ADVANCED_STATUSES.DESCRIPTION'
    ),
  },
  {
    key: 'quickCreateFromConversation',
    title: t(
      'GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.QUICK_CREATE_FROM_CONVERSATION.TITLE'
    ),
    description: t(
      'GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.QUICK_CREATE_FROM_CONVERSATION.DESCRIPTION'
    ),
  },
]);

watch(
  currentAccount,
  account => {
    features.value = resolveProductsOrdersFeatures(account?.settings);
  },
  { immediate: true, deep: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        products_orders_features: {
          open_conversation_link: features.value.openConversationLink,
          timeline: features.value.timeline,
          advanced_statuses: features.value.advancedStatuses,
          quick_create_from_conversation:
            features.value.quickCreateFromConversation,
        },
      },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.API.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.API.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.TITLE') }}
      </h3>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.NOTE') }}
      </p>
    </div>

    <div class="px-5 py-4">
      <form class="grid gap-4" @submit.prevent="handleSubmit">
        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 divide-y divide-n-weak"
        >
          <div
            v-for="item in featureItems"
            :key="item.key"
            class="flex items-start justify-between gap-4 p-4"
          >
            <div class="space-y-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ item.title }}
              </p>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ item.description }}
              </p>
            </div>
            <Switch v-model="features[item.key]" />
          </div>
        </div>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('GENERAL_SETTINGS.FORM.PRODUCTS_ORDERS_FEATURES.SAVE')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
